import React, { useRef, useState } from "react";
import { Image as ImageIcon, Video as VideoIcon, Music, X, Loader2, Play } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import MusicPicker from "./MusicPicker";

/**
 * MediaAttachmentBar — photo/video/music upload helpers for composers.
 * Used inside Timeline and Community compose forms. Calls back with
 * { image_url, video_url, music } that should be merged into the post body.
 */
export default function MediaAttachmentBar({ value = {}, onChange, compact = false }) {
  const fileImage = useRef(null);
  const fileVideo = useRef(null);
  const [uploading, setUploading] = useState("");
  const [musicOpen, setMusicOpen] = useState(false);

  const setField = (patch) => onChange?.({ ...value, ...patch });

  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) return toast.error("Image too large (max 20MB)");
    setUploading("image");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `/api/files/${data.storage_path}`;
      setField({ image_url: url });
      toast.success("Photo attached");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(""); }
  };

  const uploadVideo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 200 * 1024 * 1024) return toast.error("Video too large (max 200MB)");
    setUploading("video");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `/api/files/${data.storage_path}`;
      setField({ video_url: url });
      toast.success("Video attached");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(""); }
  };

  const removeImage = () => setField({ image_url: null });
  const removeVideo = () => setField({ video_url: null });
  const removeMusic = () => setField({ music: null });

  return (
    <div data-testid="media-attachment-bar" className="mt-2">
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <button type="button" data-testid="attach-photo-btn" onClick={() => fileImage.current?.click()} disabled={!!uploading} className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 px-3 py-1.5 rounded-full disabled:opacity-50">
          {uploading === "image" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
          Photo
        </button>
        <button type="button" data-testid="attach-video-btn" onClick={() => fileVideo.current?.click()} disabled={!!uploading} className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 px-3 py-1.5 rounded-full disabled:opacity-50">
          {uploading === "video" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <VideoIcon className="w-3.5 h-3.5" />}
          Video
        </button>
        <button type="button" data-testid="attach-music-btn" onClick={() => setMusicOpen(true)} className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 px-3 py-1.5 rounded-full">
          <Music className="w-3.5 h-3.5" /> Music
        </button>
        <input ref={fileImage} type="file" accept="image/*" onChange={uploadImage} className="hidden" />
        <input ref={fileVideo} type="file" accept="video/*" onChange={uploadVideo} className="hidden" />
      </div>

      {(value.image_url || value.video_url || value.music) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {value.image_url && (
            <div className="relative w-28 h-28 rounded-lg overflow-hidden border border-white/10" data-testid="attachment-image">
              <img src={value.image_url} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={removeImage} className="absolute top-1 right-1 bg-black/70 hover:bg-black text-white rounded-full p-1" data-testid="remove-image"><X className="w-3 h-3" /></button>
            </div>
          )}
          {value.video_url && (
            <div className="relative w-28 h-28 rounded-lg overflow-hidden border border-white/10 bg-black" data-testid="attachment-video">
              <video src={value.video_url} muted playsInline className="w-full h-full object-cover" />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><Play className="w-6 h-6 text-white/90 fill-white/90" /></div>
              <button type="button" onClick={removeVideo} className="absolute top-1 right-1 bg-black/70 hover:bg-black text-white rounded-full p-1" data-testid="remove-video"><X className="w-3 h-3" /></button>
            </div>
          )}
          {value.music && (
            <div className="relative flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 pr-7 max-w-full min-w-[180px]" data-testid="attachment-music">
              {value.music.album_art && <img src={value.music.album_art} alt="" className="w-10 h-10 rounded object-cover" />}
              <div className="min-w-0">
                <div className="text-white text-xs font-semibold truncate">{value.music.title}</div>
                <div className="text-white/50 text-[10px] truncate">{value.music.artist}</div>
              </div>
              <button type="button" onClick={removeMusic} className="absolute top-1 right-1 bg-black/40 hover:bg-black text-white rounded-full p-1" data-testid="remove-music"><X className="w-3 h-3" /></button>
            </div>
          )}
        </div>
      )}

      <MusicPicker open={musicOpen} onClose={() => setMusicOpen(false)} onSelect={(m) => setField({ music: m })} />
    </div>
  );
}
